import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Spin, Input } from "antd";
import { getMeterLocationList } from "../services/index";
import { useDistrict } from "../utils/DistrictContext";
import { MarkerClustererF } from "@react-google-maps/api";
import clusterIcon from "../assets/images/m1.png";
import { Radio } from "antd";
const { Search } = Input;

const containerStyle = {
  width: "100%",
  height: "86vh",
  borderRadius: "12px",
  position: "relative",
};

const defaultCenter = { lat: 29.92, lng: -95.2 };

const MapTab = () => {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredMeter, setHoveredMeter] = useState(null);
  const [activeSearchMeter, setActiveSearchMeter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [map, setMap] = useState(null);
  const { selectedDistrict } = useDistrict();
  const [filterType, setFilterType] = useState("active");

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    const fetchMeterLocations = async () => {
      try {
        // 2. Call the new function
        const response = await getMeterLocationList(filterType);

        // Based on your JSON structure, data is in response.data.meters
        const allData = response?.data?.meters || [];

        // Deduplicate
        const uniqueData = allData.filter(
          (meter, index, self) =>
            index ===
            self.findIndex(
              (m) =>
                m.location_id === meter.location_id &&
                m.meter_id === meter.meter_id,
            ),
        );

        setMeters(uniqueData);
      } catch (e) {
        console.error("Failed to fetch map data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMeterLocations();
  }, [selectedDistrict, filterType]);
  const getMeterColor = (status) => {
    switch (status?.toLowerCase()) {
      case "green":
        return "#00e5a8"; // Green
      case "red":
        return "#ff4d4f"; // Red
      case "orange":
        return "#ffa940"; // Orange
      default:
        return "#00e5a8"; // Fallback
    }
  };
  useEffect(() => {
    if (!map) return;

    // 🎯 District-specific centers
    const districtCenters = {
      FB151: { lat: 29.753417, lng: -95.861396 },
      GPMD: { lat: 29.92312, lng: -95.20392 },
    };

    const districtCenter = districtCenters[selectedDistrict];

    // ✅ If district has fixed center
    if (districtCenter) {
      map.panTo(districtCenter);
      map.setZoom(15);
      return;
    }

    // 🌍 Default: fit all markers
    if (meters.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      meters.forEach((m) => {
        if (
          m.location_latitude &&
          m.location_longitude &&
          m.location_latitude !== "0" &&
          m.location_longitude !== "0"
        ) {
          bounds.extend({
            lat: parseFloat(m.location_latitude),
            lng: parseFloat(m.location_longitude),
          });
        }
      });

      map.fitBounds(bounds);
    }
  }, [map, meters, selectedDistrict]);

  const filteredMeters = useMemo(() => {
    if (!searchQuery) return meters;

    return meters.filter(
      (m) =>
        m.meter_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.location_address?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [meters, searchQuery]);

  const adjustedMeters = useMemo(() => {
    const coordinateGroups = {};

    return filteredMeters.map((meter) => {
      const lat = parseFloat(meter.location_latitude);
      const lng = parseFloat(meter.location_longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return {
          ...meter,
          adjustedLat: lat,
          adjustedLng: lng,
        };
      }

      const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;

      if (!coordinateGroups[key]) {
        coordinateGroups[key] = 0;
      }

      const index = coordinateGroups[key];
      coordinateGroups[key] += 1;

      // small circular spread for meters at same location
      const angle = (index * Math.PI) / 4;
      const offset = 0.00003 * Math.ceil(index / 2);

      return {
        ...meter,
        adjustedLat: lat + Math.cos(angle) * offset,
        adjustedLng: lng + Math.sin(angle) * offset,
      };
    });
  }, [filteredMeters]);

  const onSearch = (value) => {
    setSearchQuery(value);
    const found = meters.find(
      (m) =>
        m.meter_id.toLowerCase() === value.toLowerCase() ||
        m.location_address?.toLowerCase().includes(value.toLowerCase()),
    );

    if (found && map) {
      setActiveSearchMeter(found);
      map.panTo({
        lat: parseFloat(found.location_latitude),
        lng: parseFloat(found.location_longitude),
      });
      map.setZoom(17);
    }
  };

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  if (!isLoaded || loading) {
    return (
      <div className="h-[600px] flex items-center justify-center">
        <Spin size="large" tip="Loading Pins..." />
      </div>
    );
  }

  const selectedDisplayMeter = activeSearchMeter || hoveredMeter;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* TOP CENTER: SEARCH BAR ONLY */}
      <div
        style={{
          position: "absolute",
          top: "15px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          width: "80%",
          maxWidth: "400px",
        }}
      >
        <Search
          placeholder="Search Meter ID or Address..."
          allowClear
          enterButton="Search"
          size="large"
          onSearch={onSearch}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!e.target.value) setActiveSearchMeter(null);
          }}
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}
        />
      </div>

      {/* BOTTOM RIGHT: TOGGLE (Near Zoom Icons) */}
      <div
        style={{
          position: "absolute",
          bottom: "100px", // Adjust this value so it sits right above the +/- zoom buttons
          right: "10px",
          zIndex: 10,
        }}
      >
        <Radio.Group
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          buttonStyle="solid"
          size="middle"
          style={{
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column", // Stacked looks cleaner near zoom controls
          }}
        >
          <Radio.Button
            value="active"
            style={{
              borderRadius: "4px 4px 0 0",
              textAlign: "center",
            }}
          >
            Active
          </Radio.Button>
          <Radio.Button
            value="all"
            style={{
              borderRadius: "0 0 4px 4px",
              textAlign: "center",
            }}
          >
            All
          </Radio.Button>
        </Radio.Group>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={15}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
        }}
        onClick={() => {
          setActiveSearchMeter(null);
          setHoveredMeter(null);
        }}
      >
        <MarkerClustererF
          options={{
            zoomOnClick: false,
            maxZoom: 18, // after this, clusters break into markers
            styles: [
              {
                url: clusterIcon,
                height: 53,
                width: 53,
                textColor: "#ffffff",
                textSize: 10,
              },
            ],
          }}
          onClick={(cluster) => {
            if (!map) return;

            const currentZoom = map.getZoom() || 15;
            const center = cluster.getCenter();

            if (center) {
              map.panTo(center);
            }

            // step-by-step zoom
            if (currentZoom < 16) {
              map.setZoom(16); // 1st click
            } else if (currentZoom < 18) {
              map.setZoom(18); // 2nd click
            } else if (currentZoom < 20) {
              map.setZoom(20); // 3rd click
            } else {
              map.setZoom(21); // final zoom to show separated meters
            }
          }}
        >
          {(clusterer) =>
            adjustedMeters.map((meter) => {
              const meterColor = getMeterColor(meter.operational);
              const position = {
                lat: meter.adjustedLat,
                lng: meter.adjustedLng,
              };

              if (isNaN(position.lat) || isNaN(position.lng)) return null;

              return (
                <Marker
                  key={`${meter.location_id}-${meter.meter_id}`}
                  position={position}
                  clusterer={clusterer}
                  icon={{
                    url:
                      "data:image/svg+xml;base64," +
                      btoa(`
                      <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="31" fill="white" />
                        <circle cx="32" cy="32" r="27" fill="#0a1628" />
                        <path d="M18 40 A 18 18 0 1 1 46 40" fill="none" stroke="${meterColor}" stroke-width="5" stroke-linecap="round" />
                        <line x1="32" y1="32" x2="44" y2="20" stroke="${meterColor}" stroke-width="7" stroke-linecap="round" />
                        <circle cx="32" cy="32" r="5" fill="white" />
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20),
                  }}
                  onMouseOver={() => {
                    if (!activeSearchMeter) setHoveredMeter(meter);
                  }}
                  onMouseOut={() => {
                    if (!activeSearchMeter) setHoveredMeter(null);
                  }}
                  onClick={() => {
                    setActiveSearchMeter(meter);
                    setHoveredMeter(null);
                    map.panTo(position);
                    map.setZoom(20);
                  }}
                />
              );
            })
          }
        </MarkerClustererF>

        {selectedDisplayMeter && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedDisplayMeter.location_latitude),
              lng: parseFloat(selectedDisplayMeter.location_longitude),
            }}
            onCloseClick={() => setActiveSearchMeter(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
          >
            <div className="p-2 text-black max-w-[200px]">
              <div className="font-bold text-blue-600 border-b border-gray-200 pb-1 mb-1">
                Meter ID: {selectedDisplayMeter.meter_id}
              </div>
              <div className="text-[11px] leading-tight text-gray-700 font-medium">
                {selectedDisplayMeter.location_address || "No address provided"}
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default MapTab;
